/**
 * Phase 36 — start an experiment: pick a LANE of the pool (Phase 40 — a
 * gateway with its own sender IDs), the share of the drops it gets, the
 * days, the metric to compare on — in the step rows of Schedule — then
 * Start. The pool needs two lanes (one to compare with).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { CalendarRange, Percent, RadioTower, Scale } from 'lucide-react';
import { DROP_GEN_EXPERIMENT_MAX_DAYS, DROP_GEN_EXPERIMENT_MAX_SHARE, type DropGenExperimentCreate, type DropGenExperimentMetric, type DropGenTextMix, laneLabel } from '@trafficpulse/shared';

import { Button, SearchableSelect } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { DraftPoolRow } from '../../../../drop-generator.types';
import { NumberStepper } from '../../general/controls/NumberStepper';
import { StepRow, ValueLine } from '../../general/controls/StepRow';
import { GatewayChip } from '../zones/GatewayChip';
import { METRIC_OPTIONS } from './experiment-copy';
import { ExperimentStory } from './story/ExperimentStory';

const SHARES = [10, 20, 30] as const;
const DAYS = [3, 7, 14] as const;
const CHIP = 'flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors';

interface Props {
  pool: DraftPoolRow[];
  routeName: (routeId: number) => string;
  canManage: boolean;
  busy: boolean;
  error: string | null;
  onStart: (body: DropGenExperimentCreate) => void;
  /** Phase 37 — the texts the reserved drops send. */
  proven?: DropGenTextMix;
}

export const ExperimentForm: FC<Props> = ({ pool, routeName, canManage, busy, error, onStart, proven }) => {
  const [laneId, setLaneId] = useState<string | null>(pool[pool.length - 1]?.id ?? null);
  const [sharePct, setShare] = useState(20);
  const [days, setDays] = useState(7);
  const [metric, setMetric] = useState<DropGenExperimentMetric>('deliveredRate');
  const lane = pool.find((l) => l.id === laneId) ?? null;
  const ready = lane !== null && pool.length >= 2 && canManage;
  return (
    <div className="divide-y divide-border/50">
      <StepRow icon={RadioTower} label="Lane" hint="The lane to test — one row of the pool: a gateway with its own sender IDs. The other drops of the day go to the pool's other lanes, so the two sides never mix.">
        <ValueLine>
          {pool.map((row, i) => (
            <button key={row.id} type="button" aria-pressed={laneId === row.id} onClick={() => setLaneId(row.id)} className={cn(CHIP, laneId === row.id ? 'border-primary bg-primary/10 text-foreground' : 'border-border/60 text-muted-foreground hover:bg-muted')}>
              <GatewayChip index={i} />
              {laneLabel(row, routeName(row.routeId))}
            </button>
          ))}
          {pool.length < 2 ? <span className="text-xs text-warning">This needs another gateway to compare with — add one under Gateways and save.</span> : null}
        </ValueLine>
      </StepRow>
      <StepRow icon={Percent} label="Share" hint={`The share of each day's drops reserved for the lane, by largest remainder over the day (1–${DROP_GEN_EXPERIMENT_MAX_SHARE} %). With few drops a small share may reserve none on some days.`}>
        <ValueLine>
          <NumberStepper value={sharePct} onChange={setShare} min={1} max={DROP_GEN_EXPERIMENT_MAX_SHARE} step={5} unit="%" ariaLabel="Share of the drops" presets={SHARES} />
          <span className="text-xs text-muted-foreground">of each day's drops</span>
        </ValueLine>
      </StepRow>
      <StepRow icon={CalendarRange} label="Days" hint={`From today, on the country's clock, up to ${DROP_GEN_EXPERIMENT_MAX_DAYS} days. The result is compared every pass and frozen after the last day.`}>
        <ValueLine>
          <NumberStepper value={days} onChange={setDays} min={1} max={DROP_GEN_EXPERIMENT_MAX_DAYS} step={1} ariaLabel="Days" presets={DAYS} />
          <span className="text-xs text-muted-foreground">{days === 1 ? 'today only' : `today and the next ${days - 1} day${days === 2 ? '' : 's'}`}</span>
        </ValueLine>
      </StepRow>
      <StepRow icon={Scale} label="Compare on" hint="The rate the two sides are compared on — the judge's own rates (delivered, bot, real CTR, undeliverable, CTR, CR). Money metrics arrive with their phase.">
        <ValueLine>
          <SearchableSelect options={METRIC_OPTIONS} value={metric} onChange={(v) => setMetric(v as DropGenExperimentMetric)} className="w-56" />
        </ValueLine>
      </StepRow>
      <div className="pt-3">
        <ExperimentStory pool={pool} laneId={laneId} sharePct={sharePct} days={days} metric={metric} routeName={routeName} proven={proven} />
      </div>
      <div className="flex flex-wrap items-center gap-3 pt-3">
        <Button size="sm" disabled={!ready || busy} onClick={() => lane !== null && onStart({ routeId: lane.routeId, gatewayRowId: lane.id, sharePct, days, metric })}>
          {busy ? 'Starting…' : 'Start the experiment'}
        </Button>
        {!canManage ? <span className="text-xs text-muted-foreground">You need the manage permission to start one.</span> : null}
        {error ? <span className="text-xs text-destructive">{error}</span> : null}
      </div>
    </div>
  );
};
